import { useCallback } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardBody,
  IconButton,
  Tooltip,
  TooltipProvider,
  CodeBlock,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { env } from '@/config/env';
import { tokenStorage } from '@/lib/token-storage';
import { HlsPreviewPlayer } from '../../../_shared/HlsPreviewPlayer';
import styles from './JobDetailMasterUrlCard.module.css';

export interface OutputProfile {
  name: string;
  /** Per-profile HLS rendition playlist. */
  hls?: string;
  /** Per-profile MP4 file. */
  mp4?: string;
}

/** One preset's outputs: the HLS master manifest, plus each profile's HLS
 *  rendition + MP4 file grouped together (so a profile name appears once). */
export interface OutputGroup {
  presetName: string;
  hlsManifest?: string;
  profiles: OutputProfile[];
}

interface Props {
  masterUrl: string;
  outputs?: OutputGroup[];
  /** Portal job UUID — needed to build the authenticated preview-proxy URL. */
  jobId?: string;
}

/** A master_url the vendor returns as an absolute URL is public (CDN) and plays
 *  directly; a relative path lives on a credentialed distribution storage and
 *  must go through the @mgmt preview proxy. */
const isAbsoluteUrl = (u: string) => /^https?:\/\//i.test(u);

/** A labelled URL row with a copy button (used for the HLS manifest). */
function UrlRow({ label, url, onCopy }: { label: string; url: string; onCopy: (u: string) => void }) {
  return (
    <div className={styles.profileItem}>
      <span className={styles.profileName}>{label}</span>
      <span className={styles.profileUrl} title={url}>{url || '—'}</span>
      {url && (
        <Tooltip content="Copy" side="top">
          <IconButton aria-label={`Copy ${label} URL`} variant="ghost" size="sm" onClick={() => onCopy(url)}>
            <Copy size={12} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}

/** A single URL + copy inside a profile-row cell; renders "—" when absent. */
function UrlCell({ url, label, onCopy }: { url?: string; label: string; onCopy: (u: string) => void }) {
  if (!url) return <span className={styles.urlCellEmpty}>—</span>;
  return (
    <span className={styles.urlCell}>
      <span className={styles.profileUrl} title={url}>{url}</span>
      <Tooltip content="Copy" side="top">
        <IconButton aria-label={`Copy ${label} URL`} variant="ghost" size="sm" onClick={() => onCopy(url)}>
          <Copy size={12} />
        </IconButton>
      </Tooltip>
    </span>
  );
}

export function JobDetailMasterUrlCard({ masterUrl, outputs = [], jobId }: Props) {
  const toast = useToast();

  // Absolute (CDN) → play as-is. Relative (storage) → proxy through @mgmt, which
  // resolves the storage credentials server-side. The proxy is authenticated, so
  // every hls.js request carries the console's Bearer token.
  const proxied = !isAbsoluteUrl(masterUrl) && !!jobId;
  const previewSrc = proxied
    ? `${env.serviceApiV3Url}/media/jobs/${jobId}/preview${masterUrl.startsWith('/') ? '' : '/'}${masterUrl}`
    : masterUrl;
  // Stable identity — hls.js tears down and re-inits whenever xhrSetup changes,
  // so this must not be a fresh closure on every render.
  const attachAuth = useCallback((xhr: XMLHttpRequest) => {
    const token = tokenStorage.access;
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  }, []);
  const previewXhrSetup = proxied ? attachAuth : undefined;

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Section title="Master URL" description="Primary playback manifest URL for this job." style={{ marginBottom: 0 }}>
      <TooltipProvider>
        <Card flat>
          <CardBody>
            <div className={styles.urlRow}>
              <CodeBlock code={masterUrl} copyable={false} wrap maxHeight={60} />
              <div className={styles.urlActions}>
                <Tooltip content="Copy URL" side="top">
                  <IconButton aria-label="Copy master URL" variant="ghost" size="sm" onClick={() => void copy(masterUrl)}>
                    <Copy size={14} />
                  </IconButton>
                </Tooltip>
                {/* A relative master URL (storage-distributed) isn't openable
                    standalone — it only resolves through the authenticated proxy. */}
                {isAbsoluteUrl(masterUrl) && (
                  <Tooltip content="Open in new tab" side="top">
                    <IconButton
                      aria-label="Open master URL"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(masterUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink size={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>

            {outputs.length > 0 && (
              <div className={styles.outputsWrap}>
                <p className={styles.profilesLabel}>Outputs</p>
                <Tabs defaultValue="preset-0">
                  <TabsList>
                    {outputs.map((g, gi) => (
                      <TabsTrigger key={gi} value={`preset-${gi}`}>{g.presetName}</TabsTrigger>
                    ))}
                  </TabsList>

                  {outputs.map((g, gi) => (
                    <TabsContent key={gi} value={`preset-${gi}`}>
                      <div className={styles.outputBody}>
                        {g.hlsManifest && (
                          <div className={styles.profileList}>
                            <UrlRow label="HLS Manifest" url={g.hlsManifest} onCopy={copy} />
                          </div>
                        )}

                        {g.profiles.length > 0 && (
                          <div className={styles.outputTable}>
                            <div className={styles.outputHead}>
                              <span>Profile</span>
                              <span>HLS</span>
                              <span>MP4</span>
                            </div>
                            {g.profiles.map((p, i) => (
                              <div key={i} className={styles.outputRow}>
                                <span className={styles.profileName}>{p.name}</span>
                                <UrlCell url={p.hls} label={`${p.name} HLS`} onCopy={copy} />
                                <UrlCell url={p.mp4} label={`${p.name} MP4`} onCopy={copy} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Inline HLS preview — VOD manifest, non-live. Relative master URLs are
            streamed through the authenticated @mgmt preview proxy. */}
        <HlsPreviewPlayer
          src={previewSrc}
          live={false}
          title="Preview"
          description="HLS playback for the master manifest of this job."
          emptyMessage="No manifest URL available for preview."
          statsDescription="hls.js playback metrics for this VOD job."
          xhrSetup={previewXhrSetup}
          copyUrl={masterUrl}
        />
      </TooltipProvider>
    </Section>
  );
}
